import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ProviderAPIClient from '../ProviderAPIClient'
import { useAuthorization } from '@/features/authorization/authorizationHooks'
import { homePathForPermissions } from '@/features/auth/homeRouting'

/**
 * Standalone callback handler for Auth0 passwordless authentication
 * Waits for Auth0 authentication to complete, then checks if it's a first-time login
 * Redirects to /welcome for first-time users, else to the permission-aware home
 * (`/cases` for org-less case-only users, `/orders` for org members).
 */
const StandaloneCallback: React.FC = () => {
  const { isAuthenticated, isLoading, error, getAccessTokenSilently } = useAuth0()
  const { permissions, isLoading: authzLoading } = useAuthorization()
  const navigate = useNavigate()
  const [retryCount, setRetryCount] = useState(0)
  const maxRetries = 15

  useEffect(() => {
    let active = true
    let retryTimeout: ReturnType<typeof setTimeout> | null = null

    const handleCallback = async () => {
      try {
        await Sentry.startSpan(
          {
            op: 'auth.callback',
            name: 'Standalone Auth0 Callback',
          },
          async (span) => {
            span.setAttribute('auth.flow', 'standalone')

            // If there's an error, log it and show error state
            if (error) {
              span.setAttribute('error', true)
              Sentry.captureException(new Error(`Auth0 callback error: ${error.message || error}`), {
                tags: {
                  operation: 'auth.callback',
                  flow: 'standalone',
                },
              })
              return
            }

            // Wait for Auth0 to finish loading
            if (isLoading) {
              if (retryCount < maxRetries && active) {
                const delay = Math.min(500 * Math.pow(1.5, retryCount), 2000)
                retryTimeout = setTimeout(() => {
                  if (active) {
                    setRetryCount((prev) => prev + 1)
                  }
                }, delay)
              }
              return
            }

            // Check if authenticated
            if (!isAuthenticated) {
              if (retryCount < maxRetries && active) {
                const delay = Math.min(500 * Math.pow(1.5, retryCount), 2000)
                retryTimeout = setTimeout(() => {
                  if (active) {
                    setRetryCount((prev) => prev + 1)
                  }
                }, delay)
              } else if (active) {
                // Max retries reached, redirect to login
                span.setAttribute('max_retries_reached', true)
                navigate('/login', { replace: true })
              }
              return
            }

            // Authentication successful - check if first-time login
            try {
              // Initialize API client with Auth0 token getter
              const apiClient = ProviderAPIClient.getInstance(getAccessTokenSilently)

              // Call backend to check if this is first-time login
              const firstLoginResponse = await apiClient.get('/auth/check-first-login')

              if (!firstLoginResponse.ok) {
                throw new Error(`Failed to check first login: ${firstLoginResponse.status}`)
              }

              const firstLoginData = await firstLoginResponse.json()
              const isFirstLogin = firstLoginData.isFirstLogin

              span.setAttribute('is_first_login', isFirstLogin)
              span.setAttribute('logins_count', firstLoginData.loginsCount)

              if (active) {
                // Navigate to welcome page for first-time users, otherwise to
                // the permission-aware home. For returning users, wait until the
                // authorization context has resolved permissions so a case-only
                // (org-less) user is routed to /cases rather than dead-ending on
                // /orders. While perms are still loading, retry (bounded).
                if (isFirstLogin) {
                  navigate('/welcome', { replace: true })
                } else if (authzLoading) {
                  if (retryCount < maxRetries) {
                    const delay = Math.min(500 * Math.pow(1.5, retryCount), 2000)
                    retryTimeout = setTimeout(() => {
                      if (active) {
                        setRetryCount((prev) => prev + 1)
                      }
                    }, delay)
                  } else {
                    // Perms never resolved in time — safe default.
                    navigate('/orders', { replace: true })
                  }
                } else {
                  navigate(homePathForPermissions(permissions), { replace: true })
                }
              }
            } catch (firstLoginError) {
              Sentry.captureException(firstLoginError, {
                tags: {
                  operation: 'auth.check_first_login',
                  flow: 'standalone',
                },
              })
              // On error checking first login, default to orders page (safe fallback)
              if (active) {
                navigate('/orders', { replace: true })
              }
            }
          }
        )
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            operation: 'auth.callback',
            flow: 'standalone',
          },
        })
        // On error, still try to navigate to orders
        if (active) {
          navigate('/orders', { replace: true })
        }
      }
    }

    handleCallback()

    return () => {
      active = false
      if (retryTimeout) {
        clearTimeout(retryTimeout)
      }
    }
  }, [isAuthenticated, isLoading, error, navigate, getAccessTokenSilently, retryCount, permissions, authzLoading])

  // Show error state
  if (error && !isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md p-8">
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
            <div className="mt-8">
              <div className="text-center">
                <div className="mb-4 text-red-600">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">Authentication Error</h3>
                <p className="mb-4 text-sm text-gray-600">
                  {error.message || 'An error occurred during authentication'}
                </p>
                <button
                  onClick={() => navigate('/login', { replace: true })}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  Return to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show loading state
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md p-8">
        <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
          <div className="mt-8">
            <div className="text-center">
              <div
                className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                  Loading...
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600">Completing authentication...</p>
              {retryCount > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Attempt {retryCount} of {maxRetries}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StandaloneCallback
